/**
 * Shopify Multi-Cart System
 * Allows customers to create, save, and manage multiple shopping carts
 */

class MultiCartManager {
  constructor() {
    this.storageKey = 'shopify_multi_carts';
    this.currentCartKey = 'current_cart_id';
    this.init();
  }

  init() {
    // Ensure localStorage has cart structure
    if (!localStorage.getItem(this.storageKey)) {
      localStorage.setItem(this.storageKey, JSON.stringify({}));
    }
    
    // Create default cart if none exists
    const carts = this.getAllCarts();
    if (Object.keys(carts).length === 0) {
      this.createCart('My Cart');
    }
  }

  // Generate unique ID for carts
  generateId() {
    return 'cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Get all saved carts
  getAllCarts() {
    return JSON.parse(localStorage.getItem(this.storageKey)) || {};
  }

  // Save carts to localStorage
  saveCarts(carts) {
    localStorage.setItem(this.storageKey, JSON.stringify(carts));
  }

  // Get current active cart ID
  getCurrentCartId() {
    return localStorage.getItem(this.currentCartKey);
  }

  // Set current active cart
  setCurrentCartId(cartId) {
    localStorage.setItem(this.currentCartKey, cartId);
  }

  // Create a new cart
  async createCart(name = 'New Cart') {
    const cartId = this.generateId();
    const carts = this.getAllCarts();
    
    // Get current Shopify cart
    const currentCart = await this.getShopifyCart();
    
    carts[cartId] = {
      id: cartId,
      name: name,
      items: currentCart.items || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    this.saveCarts(carts);
    this.setCurrentCartId(cartId);
    
    return cartId;
  }

  // Get Shopify cart via Ajax API
  async getShopifyCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [] };
    }
  }

  // Clear Shopify cart
  async clearShopifyCart() {
    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  }

  // Add items to Shopify cart
  async addItemsToShopifyCart(items) {
    if (!items || items.length === 0) return true;

    try {
      const formattedItems = items.map(item => ({
        id: item.variant_id || item.id,
        quantity: item.quantity,
        properties: item.properties || {}
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: formattedItems })
      });

      return response.ok;
    } catch (error) {
      console.error('Error adding items to cart:', error);
      return false;
    }
  }

  // Save current Shopify cart to a saved cart
  async saveCurrentCart(cartId) {
    const carts = this.getAllCarts();
    if (!carts[cartId]) return false;

    const shopifyCart = await this.getShopifyCart();
    
    carts[cartId].items = shopifyCart.items;
    carts[cartId].updatedAt = new Date().toISOString();
    
    this.saveCarts(carts);
    return true;
  }

  // Switch to a different cart
  async switchCart(cartId) {
    const carts = this.getAllCarts();
    if (!carts[cartId]) {
      console.error('Cart not found');
      return false;
    }

    // Save current cart before switching
    const currentCartId = this.getCurrentCartId();
    if (currentCartId && carts[currentCartId]) {
      await this.saveCurrentCart(currentCartId);
    }

    // Clear Shopify cart
    await this.clearShopifyCart();

    // Load new cart
    const newCart = carts[cartId];
    if (newCart.items && newCart.items.length > 0) {
      await this.addItemsToShopifyCart(newCart.items);
    }

    // Set as current cart
    this.setCurrentCartId(cartId);
    
    // Reload page to reflect changes
    window.location.reload();
    
    return true;
  }

  // Rename a cart
  renameCart(cartId, newName) {
    const carts = this.getAllCarts();
    if (!carts[cartId]) return false;

    carts[cartId].name = newName;
    carts[cartId].updatedAt = new Date().toISOString();
    
    this.saveCarts(carts);
    return true;
  }

  // Delete a cart
  async deleteCart(cartId) {
    const carts = this.getAllCarts();
    if (!carts[cartId]) return false;

    delete carts[cartId];
    this.saveCarts(carts);

    // If deleting current cart, switch to first available or create new one
    if (this.getCurrentCartId() === cartId) {
      const remainingCarts = Object.keys(carts);
      if (remainingCarts.length > 0) {
        await this.switchCart(remainingCarts[0]);
      } else {
        await this.createCart('My Cart');
      }
    }

    return true;
  }

  // Get cart by ID
  getCart(cartId) {
    const carts = this.getAllCarts();
    return carts[cartId] || null;
  }

  // Get total item count for a cart
  getCartItemCount(cartId) {
    const cart = this.getCart(cartId);
    if (!cart || !cart.items) return 0;
    
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  }

  // Sync current Shopify cart with active saved cart
  async syncCurrentCart() {
    const currentCartId = this.getCurrentCartId();
    if (!currentCartId) return;

    await this.saveCurrentCart(currentCartId);
  }
}

// Initialize the multi-cart manager
window.multiCartManager = new MultiCartManager();

// Auto-sync cart when items change (optional - you can enable this)
// setInterval(() => {
//   window.multiCartManager.syncCurrentCart();
// }, 5000); // Sync every 5 seconds
